// src/hooks/useRetourAbsence.ts — SCE Issues
// Le récap « retour de vacances » de l'accueil (28/10, utils/retourAbsence).
//
// Tant que le retour court (bloc retourAbsence de Prefs, noté par useAccueil) :
//  - lit les événements de la période d'absence (une lecture, triée par date)
//    et, pour qui y a accès, les actions FNC-T (cache de session de fnct.api) ;
//  - calcule le récap selon le rôle AFFICHÉ ;
//  - l'ouvre d'office UNE fois, puis le laisse au menu du compte ;
//  - retient les étapes cochées, relues avant écriture.
// En bascule de rôle : lu, ouvert seulement depuis le menu, rien d'écrit (les
// coches restent dans la page).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { listerEvenementsDepuis } from '@/api/commentaires.api'
import { chargerFnct } from '@/api/fnct.api'
import { modifierRetourAbsence } from '@/api/userPrefs.api'
import { NOUVEAUTES } from '@/config/nouveautes.config'
import { FNCT_MASQUEE } from '@/config/fnct.config'
import { getCoordinateurs, pilotesDuMetier } from '@/config/coordinateurs.config'
import { METIERS_ORDRE } from '@/config/metiers.config'
import type { ClePerimetre } from '@/config/sujets.config'
import type { Commentaire, Issue, Metier } from '@/types/issue.types'
import { accesFnct as aAccesFnct } from '@/utils/droitsFnct'
import { aujourdhuiParis } from '@/utils/fnct'
import { mesActions, type MesActions } from '@/utils/mesFnct'
import {
  blocEnCours,
  carteAReprendre,
  marquerEtape,
  noterProposees,
  recapAbsence,
  recapVide,
  type BlocRetourAbsence,
  type Coequipier,
  type Recap,
  type RoleRecap,
} from '@/utils/retourAbsence'
import {
  annoncerDisponible,
  noterReprise,
  oublierDemande,
  oublierReprise,
  remplacerBloc,
  useEtatRetour,
} from '@/features/retour-absence/etat'
import { useFicheEnSurimpression } from '@/components/layout/ModaleRoute'

export interface EntreeRetourAbsence {
  upn: string
  /** Nom de la personne affichée, et celui des lignes FNC-T (« moi »). */
  nom: string
  prenom: string
  identites: readonly string[]
  role: RoleRecap | null
  perimetre: readonly ClePerimetre[]
  issues: readonly Issue[]
  mesIds: ReadonlySet<string>
  suivis: readonly Issue[]
  enVueRole: boolean
  maintenant: number
}

export interface RetourAbsence {
  recap: Recap | null
  ouvert: boolean
  /** La carte où (r)ouvrir le récap. */
  carte: number
  /** Rangé à gauche, une fiche ouverte à côté (grand écran). */
  aCote: boolean
  /** Une fiche est ouverte en surimpression. */
  ficheOuverte: boolean
  ouvrir: () => void
  fermer: () => void
  marquer: (id: string, fait: boolean) => void
  /** Avant d'ouvrir une fiche ou une page depuis la carte `carte`. */
  partirVers: (carte: number, aCoteDeLaFiche: boolean) => void
}

let instancesAccueil = 0

/** Les autres pilotes de mes métiers, avec ce qu'ils y sont (titulaire, suppléant). */
function coequipiersDe(upn: string, perimetre: readonly ClePerimetre[]): Coequipier[] {
  const moi = upn.toLowerCase()
  const parUpn = new Map<string, Coequipier>()
  for (const m of METIERS_ORDRE.filter((x): x is Metier => perimetre.includes(x))) {
    const titulaires = new Set(getCoordinateurs(m).map((c) => c.upn.toLowerCase()))
    for (const p of pilotesDuMetier(m)) {
      const cle = p.upn.toLowerCase()
      if (cle === moi) continue
      const c = parUpn.get(cle) ?? { nom: p.nom, upn: cle, role: titulaires.has(cle) ? 'titulaire' : 'suppleant', metiers: [] }
      c.metiers.push(m)
      parUpn.set(cle, c)
    }
  }
  return [...parUpn.values()]
}

export function useRetourAbsence(e: EntreeRetourAbsence): RetourAbsence {
  const { upn: upnEtat, bloc: blocEtat, ouvertureDemandee, reprise } = useEtatRetour()
  const bloc = upnEtat === e.upn ? blocEtat : null
  const actif = !!e.role && blocEnCours(bloc, e.maintenant)
  const avecFnct = actif && !FNCT_MASQUEE && aAccesFnct(e.upn)

  // ─── Lectures de la période ───
  const [evenements, setEvenements] = useState<{ cle: string; liste: Commentaire[] } | null>(null)
  const cleLecture = actif && bloc ? `${e.upn}|${bloc.depuis}` : ''
  useEffect(() => {
    if (!cleLecture || !bloc) return
    let annule = false
    listerEvenementsDepuis(bloc.depuis)
      .then((liste) => {
        if (!annule) setEvenements({ cle: cleLecture, liste })
      })
      .catch((err: unknown) => {
        // Le récap se passe de l'activité plutôt que de ne pas s'ouvrir.
        // eslint-disable-next-line no-console
        console.warn('[retour] événements de la période indisponibles', err)
        if (!annule) setEvenements({ cle: cleLecture, liste: [] })
      })
    return () => {
      annule = true
    }
    // bloc.depuis est dans la clé.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleLecture])

  const [actions, setActions] = useState<{ upn: string; m: MesActions | null } | null>(null)
  useEffect(() => {
    if (!avecFnct) return
    let annule = false
    chargerFnct()
      .then((d) => {
        if (!annule) setActions({ upn: e.upn, m: mesActions(d.fncts, d.actions, { nom: e.nom, upn: e.upn }, aujourdhuiParis()) })
      })
      .catch(() => {
        if (!annule) setActions({ upn: e.upn, m: null })
      })
    return () => {
      annule = true
    }
  }, [avecFnct, e.upn, e.nom])

  const evenementsPrets = evenements && evenements.cle === cleLecture ? evenements.liste : null
  const actionsPretes = !avecFnct || (actions && actions.upn === e.upn)

  const recap = useMemo(() => {
    if (!actif || !bloc || !e.role || !evenementsPrets || !actionsPretes) return null
    return recapAbsence({
      bloc,
      maintenant: e.maintenant,
      role: e.role,
      perimetre: e.perimetre,
      moi: { nom: e.nom, prenom: e.prenom, identites: e.identites },
      issues: e.issues,
      evenements: evenementsPrets,
      mesIds: e.mesIds,
      suivis: e.suivis,
      coequipiers: e.role === 'pilote' ? coequipiersDe(e.upn, e.perimetre) : [],
      actionsFnct: avecFnct ? actions?.m ?? null : null,
      accesFnct: avecFnct,
      nouveautes: NOUVEAUTES,
    })
  }, [actif, bloc, e.role, e.maintenant, e.perimetre, e.nom, e.prenom, e.identites, e.issues, e.mesIds, e.suivis, e.upn, evenementsPrets, actionsPretes, avecFnct, actions])

  const disponible = !!recap && !recap.termine && !recapVide(recap)
  useEffect(() => {
    annoncerDisponible(disponible)
  }, [disponible])

  // ─── Écritures ───
  const ecrire = useCallback(
    (local: (b: BlocRetourAbsence) => BlocRetourAbsence) => {
      if (!bloc) return
      remplacerBloc(e.upn, local(bloc))
      if (e.enVueRole) return
      modifierRetourAbsence(e.upn, local)
        .then((b) => {
          if (b) remplacerBloc(e.upn, b)
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn('[retour] enregistrement impossible', err)
        })
    },
    [bloc, e.upn, e.enVueRole],
  )

  const [ouvert, setOuvert] = useState(false)
  const idsEtapes = recap ? recap.etapes.map((x) => x.id) : []
  const cleEtapes = idsEtapes.join(',')

  // Ouvert d'office une fois (jamais en bascule de rôle), et les étapes proposées retenues.
  const ouvertDOffice = useRef(false)
  useEffect(() => {
    if (!recap || !bloc || !disponible || bloc.vu || e.enVueRole || ouvertDOffice.current) return
    ouvertDOffice.current = true
    setOuvert(true)
    const ids = cleEtapes ? cleEtapes.split(',') : []
    ecrire((b) => noterProposees(b, ids, true))
  }, [recap, bloc, disponible, e.enVueRole, cleEtapes, ecrire])

  // Demande venue du menu du compte.
  useEffect(() => {
    if (!ouvertureDemandee || !recap || recapVide(recap)) return
    oublierDemande()
    setOuvert(true)
  }, [ouvertureDemandee, recap])

  const marquer = useCallback(
    (id: string, fait: boolean) => {
      const ids = cleEtapes ? cleEtapes.split(',') : []
      ecrire((b) => noterProposees(marquerEtape(b, id, fait), ids))
    },
    [cleEtapes, ecrire],
  )

  // ─── Aller voir une fiche, puis revenir (28/10) ───
  // Grand écran : le récap se range à gauche, la fiche s'ouvre à côté ; il
  // revient au centre quand elle se referme. Ailleurs : il se referme le temps
  // de la fiche (ou de la page) et revient sur la même carte au retour.
  const [carte, setCarte] = useState(0)
  const [aCote, setACote] = useState(false)
  const [instance] = useState(() => ++instancesAccueil)
  const ficheOuverte = useFicheEnSurimpression()

  // L'accueil remonté (téléphone, retour d'une page) : la reprise notée par un autre.
  useEffect(() => {
    if (!recap || !reprise || reprise.par === instance) return
    const c = carteAReprendre(reprise, instance, 'accueil-remonte', Date.now())
    oublierReprise()
    if (c === null) return
    setCarte(c)
    setOuvert(true)
  }, [recap, reprise, instance])

  // La fiche refermée (bureau, l'accueil est resté dessous).
  const ficheAvant = useRef(ficheOuverte)
  useEffect(() => {
    const etait = ficheAvant.current
    ficheAvant.current = ficheOuverte
    if (!etait || ficheOuverte) return
    setACote(false)
    const c = carteAReprendre(reprise, instance, 'fiche-fermee', Date.now())
    if (c !== null) {
      oublierReprise()
      setCarte(c)
      setOuvert(true)
    }
  }, [ficheOuverte, reprise, instance])

  const partirVers = useCallback(
    (carteActuelle: number, aCoteDeLaFiche: boolean) => {
      setCarte(carteActuelle)
      if (aCoteDeLaFiche) {
        setACote(true)
        return
      }
      setOuvert(false)
      noterReprise(carteActuelle, instance)
    },
    [instance],
  )

  return {
    recap,
    ouvert: ouvert && !!recap,
    carte,
    aCote: aCote && ouvert,
    ficheOuverte,
    ouvrir: useCallback(() => setOuvert(true), []),
    fermer: useCallback(() => {
      setOuvert(false)
      setACote(false)
    }, []),
    marquer,
    partirVers,
  }
}
